const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  'image/gif': '.gif',
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/svg+xml': '.svg'
};

/**
 * The reverse lookup, and wider: a file copied in the OS file manager can arrive with no MIME type
 * of its own, so its name is the only thing that says what it is. Types canvas cannot encode are
 * listed too - the caller re-encodes anything the browser can decode, so recognising the file is
 * worth more here than knowing whether the uploader accepts it.
 */
const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
  '.avif': 'image/avif',
  '.bmp': 'image/bmp',
  '.gif': 'image/gif',
  '.heic': 'image/heic',
  '.jpeg': 'image/jpeg',
  '.jpg': 'image/jpeg',
  '.png': 'image/png',
  '.svg': 'image/svg+xml',
  '.tif': 'image/tiff',
  '.tiff': 'image/tiff',
  '.webp': 'image/webp'
};

// Canvas can only re-encode to these, and only these are worth targeting. SVG and GIF are decodable
// but not encodable, so they can never be a conversion target even when acceptedFiles lists them.
const ENCODABLE_TARGETS = ['image/png', 'image/jpeg', 'image/webp'];

export function defaultNameForMimeType(mimeType: string): string {
  return `pasted-image${EXTENSION_BY_MIME_TYPE[mimeType] ?? '.png'}`;
}

/**
 * The image type a file name claims, or null when the name does not name an image. Used only when
 * the file itself carries no type - never to overrule a type the browser did supply.
 */
export function imageMimeTypeForFileName(name: string): string | null {
  const dot = name.lastIndexOf('.');
  if (dot < 0) return null;
  return MIME_TYPE_BY_EXTENSION[name.slice(dot).toLowerCase()] ?? null;
}

/**
 * The type a conversion should aim for: the first canvas-encodable type the uploader actually
 * accepts. Returns null when the uploader accepts nothing canvas can produce, so the caller
 * reports the file as unsupported rather than handing back something still unacceptable.
 */
export function pickEncodableTarget(acceptedFiles: string[]): string | null {
  return ENCODABLE_TARGETS.find(type => acceptedFiles.includes(type)) ?? null;
}

// Input types that hold no text, so a paste landing on one was never meant for the control itself.
const NON_TEXT_INPUT_TYPES = ['file', 'checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'image'];

/**
 * True when the element is somewhere the user types. A paste aimed at one of these belongs to it,
 * never to an image uploader - this is what stops a hovered thumbnail from swallowing a Ctrl+V
 * meant for a notes field when the clipboard carries both text and an image (copying from Word or
 * Excel gives you exactly that).
 */
export function isTextEntryElement(el: Element | null): boolean {
  if (!el) return false;
  if ((el as HTMLElement).isContentEditable) return true;
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  return !NON_TEXT_INPUT_TYPES.includes((el as HTMLInputElement).type);
}

/**
 * Every control that claims a document-level paste. Focus in ANY of them has to block ALL the
 * others, so they must all use the same list - a control that does not know about its neighbours
 * would happily swallow a paste aimed at one of them.
 */
export const PASTE_TARGET_SELECTOR = 'bs-form-image-upload, wmview-conversation-attachments';

/**
 * Decides whether THIS uploader owns a paste, given where focus is and whether the pointer is over
 * it. The rules, in order:
 *
 *   1. A paste aimed at a text-entry element is never ours.
 *   2. Focus wins over hover. If any uploader holds focus, only that one may claim - otherwise
 *      hovering a second uploader while typing in the first would replace both images.
 *   3. Otherwise the uploader under the pointer claims it, so a plain hover-and-paste works
 *      without first clicking (clicking opens the file dialog, which is not a way to paste).
 */
export function shouldClaimPaste(options: {
  activeElement: Element | null;
  focusInside: boolean;
  pointerInside: boolean;
  focusedUploader: Element | null;
  self: Element;
}): boolean {
  const { activeElement, focusInside, pointerInside, focusedUploader, self } = options;

  if (isTextEntryElement(activeElement)) return false;
  if (focusInside) return true;
  if (focusedUploader && focusedUploader !== self) return false;
  return pointerInside;
}

export type ClipboardReadOutcome = { status: 'image'; file: File } | { status: 'no-image' } | { status: 'unavailable' };

/**
 * Reads an image straight off the system clipboard for an explicit "Paste" button, rather than
 * waiting for a Ctrl+V the user has no way of knowing is supported.
 *
 * navigator.clipboard.read is the only way to do this, and it is not universally available: it
 * needs a secure context, Chrome prompts for permission on first use, and Firefox does not expose
 * it for images. 'unavailable' is therefore a normal outcome and the caller must tell the user to
 * fall back to Ctrl+V or the file picker, never fail silently.
 *
 * It also only ever sees image CONTENT. A file copied in the OS file manager is on the system
 * clipboard as a path, which this API deliberately does not expose - only the paste event does.
 * 'no-image' therefore does not mean the clipboard is empty, and the caller must say so rather
 * than flatly contradicting a user who did just copy a picture.
 */
export async function readImageFromSystemClipboard(): Promise<ClipboardReadOutcome> {
  const read = navigator.clipboard?.read?.bind(navigator.clipboard);
  if (!read) return { status: 'unavailable' };

  let items: ClipboardItem[];
  try {
    items = await read();
  } catch {
    // Permission denied, no user gesture, or the browser refuses image reads entirely.
    return { status: 'unavailable' };
  }

  for (const item of items) {
    const type = item.types.find(t => t.startsWith('image/'));
    if (!type) continue;
    try {
      const blob = await item.getType(type);
      return { status: 'image', file: new File([blob], defaultNameForMimeType(type), { type }) };
    } catch {
      // A listed type that will not actually produce bytes; keep looking.
    }
  }

  return { status: 'no-image' };
}

/**
 * The file as a named, typed image, or null when it is not an image at all.
 *
 * Neither half can be taken for granted. A file the OS handed over can carry an empty type -
 * Windows copies a file as a path, not as typed content - so the name is consulted as a fallback.
 * Bare image content copied out of a page arrives the other way round, typed but nameless. Both are
 * filled in here, on a copy, because everything downstream reads File.type (the accept check) and
 * File.name (the extension the upload is stored under).
 */
function asImageFile(file: File): File | null {
  const type = file.type.startsWith('image/') ? file.type : (imageMimeTypeForFileName(file.name) ?? '');
  if (!type) return null;

  const name = file.name || defaultNameForMimeType(type);
  return file.type === type && file.name === name ? file : new File([file], name, { type });
}

/**
 * Every file the clipboard is offering, deduplicated, in the order worth trying.
 *
 * The two carriers are not the same thing. Copying a FILE (Explorer, Finder, a file manager) puts it
 * in `files`; copying CONTENT (a screenshot, an image copied out of a page) puts a blob in `items`.
 * They can BOTH be populated at once - Windows leaves the copied file on the clipboard and a
 * screenshot adds a bitmap alongside it - and in that case `files` reports the older file while
 * `items` also lists the new bitmap. `items` is therefore searched first, so the thing just put on
 * the clipboard wins over the thing that was left there.
 */
function clipboardFiles(clipboardData: DataTransfer | null): File[] {
  if (!clipboardData) return [];

  const found: File[] = [];
  const seen = new Set<string>();
  const add = (file: File | null | undefined) => {
    if (!file) return;
    const key = `${file.name}|${file.size}|${file.type}`;
    if (seen.has(key)) return;
    seen.add(key);
    found.push(file);
  };

  for (const item of clipboardData.items ?? []) {
    if (item.kind !== 'file') continue;
    const blob = item.getAsFile();
    // The item still knows its type in cases where the blob it hands back does not.
    if (blob) add(blob.type ? blob : new File([blob], blob.name, { type: item.type }));
  }
  for (const file of clipboardData.files ?? []) add(file);

  return found;
}

/**
 * Pulls the pasted image out of clipboard data, regardless of whether its type is one the uploader
 * accepts - callers that care must check the type themselves (see convertImageToAcceptedType).
 *
 * A candidate the clipboard itself calls an image wins over one that is only recognised by its
 * name: a screenshot or a copied picture always arrives typed, while a file copied in the file
 * manager arrives as a path with no type at all. So when a stale copied file and a fresh screenshot
 * are both on the clipboard, the screenshot is the one taken - which is what the user just did.
 */
export function imageFileFromClipboardData(clipboardData: DataTransfer | null): File | null {
  const candidates = clipboardFiles(clipboardData);

  // Through asImageFile even though the type is already known, because a clipboard blob can arrive
  // with no name and everything downstream stores the upload under one.
  const typed = candidates.find(file => file.type.startsWith('image/'));
  if (typed) return asImageFile(typed);

  for (const file of candidates) {
    const image = asImageFile(file);
    if (image) return image;
  }

  return null;
}

/**
 * The first pasted file of ANY type, for a drop zone that takes documents rather than pictures.
 * Named when the clipboard did not name it, because the name is what the upload is stored under.
 */
export function fileFromClipboardData(clipboardData: DataTransfer | null): File | null {
  const file = clipboardFiles(clipboardData)[0];
  if (!file) return null;
  if (file.name) return file;

  return new File([file], `pasted-file${EXTENSION_BY_MIME_TYPE[file.type] ?? ''}`, { type: file.type });
}

/**
 * Re-encodes an image the browser can decode into a type the uploader accepts, so a paste or drop
 * of an unlisted type (webp is the common case for an image copied or dragged out of a browser)
 * goes through instead of being silently dropped. The user is handing over image content, not a
 * file format.
 *
 * Resolves null - meaning "report this as unsupported" - when the uploader accepts no encodable
 * type, when the bytes cannot be decoded, or when the decoded image has no dimensions. Never
 * resolves a file whose type the uploader would still reject, because the caller commits the file
 * to the input before the type is re-checked and a rejected file would leave the control reporting
 * a change it does not have.
 */
export function convertImageToAcceptedType(file: File, acceptedFiles: string[]): Promise<File | null> {
  const targetType = pickEncodableTarget(acceptedFiles);
  if (!targetType) return Promise.resolve(null);

  return new Promise(resolve => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);

      // A decodable image with no intrinsic size (some SVGs) would rasterise to an empty canvas.
      if (!image.naturalWidth || !image.naturalHeight) {
        resolve(null);
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        resolve(null);
        return;
      }

      context.drawImage(image, 0, 0);
      canvas.toBlob(blob => {
        // toBlob silently falls back to PNG for a type the browser cannot encode, so trust the
        // blob's own type rather than the one that was asked for.
        if (!blob || !acceptedFiles.includes(blob.type)) {
          resolve(null);
          return;
        }
        resolve(new File([blob], defaultNameForMimeType(blob.type), { type: blob.type }));
      }, targetType);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };

    image.src = objectUrl;
  });
}
